"use client";

import { useEffect, useState, useTransition } from "react";
import { useJobContext } from "@/components/jobs/job-context";
import { PostActionSheet } from "@/components/profile/post-action-sheet";
import { useConsent } from "@/lib/consent";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { useStickyFooterClaimed } from "@/lib/monetization/sticky-footer-claim";
import type { ProfileFieldId } from "@/lib/profile/field-config";
import { pickJobViewNudge } from "@/server/actions/profile-nudge";

const DEFAULT_DELAY_MS = 6_000;

type Props = {
	t: Dictionary;
	/** Wait this long after mount before asking the server. Default 6s. */
	delayMs?: number;
};

/**
 * Mounted on `/[locale]/jobs/[slug]` for authenticated users. Waits for the
 * reader to engage (default 6s on-page), then asks the nudge selector for
 * the highest-priority tier-1 field. Cooldowns + per-session cap inside
 * `pickNextNudge` are the source of truth for "don't spam" — the only
 * client-side knob is the engagement delay.
 *
 * Closes itself if the route unmounts before the timer fires.
 */
export function JobViewProfileNudge({ t, delayMs = DEFAULT_DELAY_MS }: Props) {
	const { isAuthenticated } = useJobContext();
	const [field, setField] = useState<ProfileFieldId | null>(null);
	const [, startTransition] = useTransition();
	const { record: consentRecord } = useConsent();
	const footerClaimed = useStickyFooterClaimed();

	useEffect(() => {
		// Signed-in readers only — the nudge targets profile completion. Guests
		// (and Googlebot, which renders the cached static HTML) never schedule it.
		// One overlay at a time: wait for the cookie banner to resolve and never
		// open while the mobile apply bar holds the bottom slot.
		if (!isAuthenticated || consentRecord === null || footerClaimed) return;
		let cancelled = false;
		const handle = setTimeout(() => {
			startTransition(async () => {
				const r = await pickJobViewNudge();
				if (cancelled) return;
				if (r.ok && r.nudge) setField(r.nudge.field);
			});
		}, delayMs);
		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [delayMs, isAuthenticated, consentRecord, footerClaimed]);

	if (!field) return null;

	return (
		<PostActionSheet
			field={field}
			surface="post_job_view"
			eyebrow={t.jobViewedEyebrow ?? "Sharper matches in 10 seconds"}
			autoDismissMs={12_000}
			t={t}
			onClose={() => setField(null)}
		/>
	);
}
